<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { X, Check } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandInput,
  CommandEmpty,
} from '@/components/ui/command'

const model = defineModel({ type: Array, required: true, default: () => [] })

const props = defineProps({
  options: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select options...'
  }
})

const inputValue = ref('')
const open = ref(false)

const handleSelect = (option) => {
  inputValue.value = ''

  const exists = model.value.some(i => i.id === option.id)
  if (exists) return

  model.value = [...model.value, option]
}

const handleUnselect = (option) => {
  model.value = model.value.filter((i) => i.id !== option.id)
}

// Optional: Keep backspace functionality
// Even though the tags are visually separate, it is usually good UX
// to allow deleting the last tag if the input is empty.
const handleKeyDown = (e) => {
  if (e.key === 'Backspace' && inputValue.value === '' && model.value.length > 0) {
    model.value = model.value.slice(0, -1)
  }
  if(open.value === false) {
    open.value = true
  }
}

const filteredOptions = computed(() => {
  return props.options.filter(
    option => !model.value.some(selected => selected.id === option.id)
  )
})

// watch the open variable for debugging
watch(open, (newVal) => {
  console.log('Dropdown open state changed:', newVal)
})


</script>

<template>
  <div class="flex flex-col gap-3">
    <div v-if="model.length > 0" class="flex flex-wrap gap-2">
      <Badge v-for="item in model" :key="item.id" variant="secondary">
        {{ item.name }}
        <button class="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2" @click="handleUnselect(item)">
          <X class="h-3 w-3 text-muted-foreground hover:text-foreground" />
        </button>
      </Badge>
    </div>

    <Command v-model:search-term="inputValue" class="overflow-visible bg-transparent">
      <div class="relative border border-input rounded-md ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <CommandInput :placeholder="placeholder" class="bg-transparent border-none focus:ring-0 outline-none w-full" @keydown="handleKeyDown" @click="open = true" @blur="open = false" />
      </div>

      <div v-if="open && filteredOptions.length > 0" class="relative mt-2">
        <div class="absolute top-0 z-10 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95">
          <CommandList class="max-h-[200px] overflow-y-auto">
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              <CommandItem v-for="option in filteredOptions" :key="option.id" :value="option.name" class="cursor-pointer" @select.prevent="handleSelect(option)" @mousedown.prevent>
                {{ option.name }}
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </div>
      </div>
    </Command>
  </div>
</template>
